import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'

const TITLE_SUFFIX = ' | E-chat'
const SEO_PATH = 'seo'

/**
 * Localized <title> and description for cabinet pages (CHAT-598).
 *
 * The server renders the English baseline for the requested URL (config/seo.php),
 * this composable keeps it in sync with SPA navigation and the cabinet language.
 *
 * Route config:
 *   meta.seo = { page: 'login' }          -> seo.pages.login.{title,description}
 *   meta.seo = { integration: 'Kommo' }   -> seo.integration_guide + shared description
 * Pages without own description fall back to seo.default.description.
 */
export const usePageMeta = () => {
    const route = useRoute()
    const { t, te } = useI18n()

    const getPageTitle = ({ page, integration }) => {
        if (integration) {
            return t(`${SEO_PATH}.integration_guide`, { name: integration })
        }

        const titleKey = `${SEO_PATH}.pages.${page}.title`

        return te(titleKey) ? t(titleKey) : t(`${SEO_PATH}.default.title`)
    }

    const getPageDescription = ({ page }) => {
        const descriptionKey = `${SEO_PATH}.pages.${page}.description`

        return te(descriptionKey) ? t(descriptionKey) : t(`${SEO_PATH}.default.description`)
    }

    const pageMeta = computed(() => {
        const seo = route.meta.seo ?? {}

        return {
            title: `${getPageTitle(seo)}${TITLE_SUFFIX}`,
            description: getPageDescription(seo),
        }
    })

    useHead({
        title: computed(() => pageMeta.value.title),
        meta: computed(() => [
            { name: 'description', content: pageMeta.value.description },
            { property: 'og:title', content: pageMeta.value.title },
            { property: 'og:description', content: pageMeta.value.description },
        ]),
    })
}
